// Leaderboards modal — 4 tabs.
// GLOBAL (default) pulls real rolling 7-day top-kill rows from the
// `leaderboard` PartyKit party via fetchGlobalLeaderboard(); falls
// back to a small mock list flagged by a "MOCK" chip when the party
// is unreachable.
// LEVEL / CRYSTALS / WIN % are local mock top-10s with a real YOU
// row pinned at the top, computed from the persistent wallet state.

import { getState, getWinRate } from '../meta/wallet';
import { fetchGlobalLeaderboard, type GlobalLeaderboard } from './leaderboard_api';

type LeaderboardTab = 'global' | 'level' | 'crystals' | 'winrate';

type LeaderboardEntry = {
  rank: number | 'you';
  name: string;
  role: 'int' | 'ext';
  value: string;
  isYou?: boolean;
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const NAMES = [
  'shadowfang', 'pixelwave',  'neonpanda',  'ghostbyte',  'luminari',
  'vantablack', 'retroqueen', 'starburst',  'frostbyte',  'emberline',
];
const ROLES: ('int' | 'ext')[] = ['int', 'ext', 'ext', 'int', 'ext', 'int', 'int', 'ext', 'int', 'ext'];

function mockData(tab: LeaderboardTab): LeaderboardEntry[] {
  return NAMES.map((n, i) => {
    const value = tab === 'level'
      ? `Lv ${50 - i * 2}`
      : tab === 'crystals'
      ? `${(25000 - i * 2300).toLocaleString()} 💎`
      : `${(87 - i * 3).toFixed(1)}%`;
    return { rank: i + 1, name: n, role: ROLES[i], value };
  });
}

// Build a YOU row from the wallet's current state for the active tab.
// Always pinned at the top regardless of actual rank vs the mock list.
function youRow(tab: LeaderboardTab): LeaderboardEntry {
  const s = getState();
  const value = tab === 'level'
    ? `Lv ${s.bestLevel}`
    : tab === 'crystals'
    ? `${s.crystals.toLocaleString()} 💎`
    : s.matchesPlayed > 0
    ? `${(getWinRate() * 100).toFixed(1)}%`
    : '—';
  // Role is unknown at the lobby level — use a generic neutral color.
  return { rank: 'you', name: 'YOU', role: 'int', value, isYou: true };
}

export function openLeaderboards(onClose?: () => void): () => void {
  let currentTab: LeaderboardTab = 'global';
  // Cached global board — lazy-fetched on first render + when tab is
  // re-opened after cache TTL. `null` = not yet fetched; after fetch
  // we stash whichever result came back (real or mock).
  let globalBoard: GlobalLeaderboard | null = null;
  let globalFetching = false;

  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed; inset: 0;
    background: rgba(2,6,23,.93);
    z-index: 9998;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    color: #e5e7eb; font: 700 14px system-ui, sans-serif;
  `;

  const title = document.createElement('div');
  title.style.cssText = `
    font: 900 28px system-ui; letter-spacing: 4px; margin-bottom: 6px;
    background: linear-gradient(90deg,#fbbf24,#fde047,#f59e0b);
    -webkit-background-clip: text; background-clip: text; color: transparent;
  `;
  title.textContent = '🏆 LEADERBOARDS';
  overlay.appendChild(title);

  const sub = document.createElement('div');
  sub.style.cssText = `opacity:.65;font-size:11px;letter-spacing:2.5px;margin-bottom:18px;`;
  sub.textContent = 'TOP 10 · WEEKLY';
  overlay.appendChild(sub);

  // Small chip that reads LIVE (green) or MOCK (amber) while on the
  // GLOBAL tab, so it's obvious when the leaderboard party is
  // unreachable and we're showing fallback rows.
  const liveChip = document.createElement('div');
  liveChip.style.cssText = `
    font: 800 10px system-ui, sans-serif;
    letter-spacing: 2px;
    padding: 3px 10px;
    border-radius: 999px;
    margin-bottom: 10px;
    display: none;
  `;
  overlay.appendChild(liveChip);

  const tabs = document.createElement('div');
  tabs.style.cssText = `
    display: flex; gap: 4px; margin-bottom: 14px;
    background: rgba(15,23,42,.85);
    padding: 4px;
    border-radius: 10px;
    border: 1px solid #334155;
  `;
  const tabDefs: { id: LeaderboardTab; label: string }[] = [
    { id: 'global',   label: 'GLOBAL'   },
    { id: 'level',    label: 'LEVEL'    },
    { id: 'crystals', label: 'CRYSTALS' },
    { id: 'winrate',  label: 'WIN %'    },
  ];
  const tabButtons: HTMLButtonElement[] = [];
  for (const t of tabDefs) {
    const btn = document.createElement('button');
    btn.textContent = t.label;
    btn.dataset.tab = t.id;
    btn.style.cssText = `
      padding: 8px 24px;
      border: 0;
      border-radius: 7px;
      font: 800 12px system-ui;
      letter-spacing: 1.5px;
      cursor: pointer;
      background: transparent;
      color: #94a3b8;
      transition: background .1s, color .1s;
    `;
    btn.addEventListener('click', () => {
      currentTab = t.id;
      renderList();
    });
    tabs.appendChild(btn);
    tabButtons.push(btn);
  }
  overlay.appendChild(tabs);

  const list = document.createElement('div');
  list.style.cssText = `
    width: 460px;
    max-width: 92vw;
    background: rgba(15,23,42,.92);
    border: 1px solid #475569;
    border-radius: 12px;
    padding: 10px 0;
    max-height: 50vh;
    overflow-y: auto;
    box-shadow: 0 16px 40px rgba(0,0,0,.6);
  `;
  overlay.appendChild(list);

  function renderList() {
    for (const btn of tabButtons) {
      const active = btn.dataset.tab === currentTab;
      btn.style.background = active ? 'rgba(56,189,248,.22)' : 'transparent';
      btn.style.color = active ? '#fde047' : '#94a3b8';
    }

    // Global tab has its own data source + loading state + chip.
    if (currentTab === 'global') {
      renderGlobalTab();
      return;
    }
    liveChip.style.display = 'none';

    const you = youRow(currentTab);
    const entries: LeaderboardEntry[] = [you, ...mockData(currentTab)];
    list.innerHTML = entries.map((e) => {
      const roleColor = e.isYou ? '#fde047' : e.role === 'int' ? '#60a5fa' : '#fb923c';
      const roleIcon = e.isYou ? '⭐' : e.role === 'int' ? '🥷' : '🎉';
      const medal = e.rank === 'you'
        ? 'YOU'
        : e.rank === 1
        ? '🥇'
        : e.rank === 2
        ? '🥈'
        : e.rank === 3
        ? '🥉'
        : `#${e.rank}`;
      const rowBg = e.isYou
        ? 'background: linear-gradient(90deg, rgba(253,224,71,.15), rgba(253,224,71,.02));'
        : '';
      const borderBottom = e.isYou
        ? 'border-bottom: 1px solid rgba(253,224,71,.45);'
        : 'border-bottom: 1px solid rgba(255,255,255,.05);';
      return `
        <div style="
          display: flex; align-items: center; gap: 14px;
          padding: 9px 22px;
          ${rowBg}
          ${borderBottom}
        ">
          <div style="width:42px;text-align:center;font:800 ${e.isYou ? '11' : '14'}px system-ui;letter-spacing:${e.isYou ? '1.5px' : '0'};color:${e.isYou ? '#fde047' : 'inherit'};">${medal}</div>
          <div style="width:22px;text-align:center;font-size:16px;">${roleIcon}</div>
          <div style="flex:1;color:${roleColor};font:${e.isYou ? '900' : '700'} 13px system-ui;letter-spacing:${e.isYou ? '1.5px' : '0'};">${e.name}</div>
          <div style="font:800 13px system-ui;color:#fde047;">${e.value}</div>
        </div>
      `;
    }).join('');
  }

  // Render the GLOBAL tab. If we don't have data yet, kick off a
  // fetch and show a loading row; on resolve, re-render. Renders the
  // mock board with an amber "MOCK" chip if the party is unreachable.
  function renderGlobalTab() {
    if (!globalBoard && !globalFetching) {
      globalFetching = true;
      list.innerHTML = `
        <div style="padding:28px;text-align:center;color:#94a3b8;font:700 12px system-ui;letter-spacing:2px;">
          LOADING…
        </div>`;
      liveChip.style.display = 'none';
      fetchGlobalLeaderboard()
        .then((board) => { globalBoard = board; })
        .catch(() => { globalBoard = { entries: [], genTs: Date.now(), windowDays: 7, mock: true }; })
        .finally(() => {
          globalFetching = false;
          if (currentTab === 'global') renderGlobalTab();
        });
      return;
    }
    if (globalFetching) return; // loading state already shown

    const board = globalBoard!;
    // Chip.
    liveChip.style.display = 'block';
    if (board.mock) {
      liveChip.textContent = '⚠ MOCK · LEADERBOARD OFFLINE';
      liveChip.style.background = 'rgba(251,146,60,.18)';
      liveChip.style.color = '#fb923c';
      liveChip.style.border = '1px solid rgba(251,146,60,.45)';
    } else {
      liveChip.textContent = '● LIVE · 7-DAY TOP KILLS';
      liveChip.style.background = 'rgba(134,239,172,.18)';
      liveChip.style.color = '#86efac';
      liveChip.style.border = '1px solid rgba(134,239,172,.45)';
    }

    if (board.entries.length === 0) {
      list.innerHTML = `
        <div style="padding:28px;text-align:center;color:#94a3b8;font:700 12px system-ui;letter-spacing:2px;">
          NO ENTRIES YET · BE THE FIRST
        </div>`;
      return;
    }

    const shown = board.entries.slice(0, 10);
    list.innerHTML = shown.map((e) => {
      const roleColor = e.role === 'int' ? '#60a5fa' : '#fb923c';
      const roleIcon = e.role === 'int' ? '🥷' : '🎉';
      const medal = e.rank === 1 ? '🥇' : e.rank === 2 ? '🥈' : e.rank === 3 ? '🥉' : `#${e.rank}`;
      const kd = e.deaths > 0 ? (e.kills / e.deaths).toFixed(1) : String(e.kills);
      const safeName = escapeHtml(e.name);
      const safeMode = escapeHtml(e.mode.toUpperCase());
      return `
        <div style="
          display: flex; align-items: center; gap: 14px;
          padding: 9px 22px;
          border-bottom: 1px solid rgba(255,255,255,.05);
        ">
          <div style="width:42px;text-align:center;font:800 14px system-ui;">${medal}</div>
          <div style="width:22px;text-align:center;font-size:16px;">${roleIcon}</div>
          <div style="flex:1;color:${roleColor};font:700 13px system-ui;">${safeName}</div>
          <div style="text-align:right;">
            <div style="font:800 13px system-ui;color:#fde047;">${e.kills} K · ${e.deaths} D</div>
            <div style="font:700 9px ui-monospace,monospace;color:#94a3b8;letter-spacing:1px;">${safeMode} · ${kd} K/D</div>
          </div>
        </div>
      `;
    }).join('');
  }

  renderList();

  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'CLOSE [ESC]';
  closeBtn.style.cssText = `
    margin-top: 18px;
    padding: 8px 22px;
    border-radius: 8px;
    border: 1px solid #475569;
    background: rgba(15,23,42,.85);
    color: #e5e7eb;
    cursor: pointer;
    font: 700 12px system-ui;
    letter-spacing: 1.5px;
  `;
  overlay.appendChild(closeBtn);

  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    removeEventListener('keydown', onKey);
    overlay.remove();
    onClose?.();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.code === 'Escape') close();
  };
  addEventListener('keydown', onKey);
  closeBtn.addEventListener('click', close);

  document.body.appendChild(overlay);
  return close;
}
